import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import 'react-slideshow-image/dist/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import { useAddFloorPlansMutation } from '../../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../../shared/AuthRoles';
import FormUpload from '../../../../shared/FormUpload';
import toast from '../../../../shared/Toast';
import { FormValues } from '../../../../types/experience';
import { useFormState } from '../useFormState';

const ExperienceFloorPlans = () => {
    const loggedInUser = UserRole();
    const { isSaveDisabled, setIsSaveDisabled } = useFormState();
    const [addFloorPlan] = useAddFloorPlansMutation();
    const { control, clearErrors, setError } = useFormContext<FormValues>();

    const handleFloorPlans = async (files: File[]) => {
        setIsSaveDisabled(true);
        try {
            const formData = new FormData();
            for (const image of files) {
                formData.append('files', image);
            }
            const requestInfo = [loggedInUser.company, formData];
            const response = await addFloorPlan(requestInfo).unwrap();
            clearErrors('floorPlans');

            return response;
        } catch (error: any) {
            console.error('Photo upload error:', error);
            if (error.status === 401) {
                setError('floorPlans', {
                    message:
                        'Authentication error. Please try logging in again.',
                });
                toast.error(
                    'Authentication error. Please try logging in again.',
                );
            } else {
                setError('floorPlans', {
                    message: 'Error uploading files. Please try again.',
                });
                toast.error('Error uploading files. Please try again.');
            }
        } finally {
            setIsSaveDisabled(false);
        }
    };

    return (
        <fieldset>
            <Stack spacing={2}>
                <div>
                    <Typography variant='h2' component='legend'>
                        Floor Plans (optional)
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }} gutterBottom>
                        Upload floor plans to give guests a better idea of the
                        space.
                    </Typography>
                </div>
                <FormUpload
                    name='floorPlans'
                    control={control}
                    onUpload={handleFloorPlans}
                    showFiles={true}
                    validation={{
                        acceptedTypes: {
                            'image/*': [
                                '.png',
                                '.jpg',
                                '.jpeg',
                                '.gif',
                                '.webp',
                            ],
                        },
                        maxFileSize: 5 * 1024 * 1024, // 5MB
                        maxFiles: 10,
                        resolution: { width: 1024, height: 683 },
                    }}
                    disabled={isSaveDisabled}
                    hasLightBox={true}
                    uploadText='Click to browse floor plans or drag and drop'
                />
            </Stack>
        </fieldset>
    );
};

export default ExperienceFloorPlans;
